import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Bookmark, Share2 } from 'lucide-react'

interface RouteNameModalProps {
  open: boolean
  /** 'save' = persisting to the signed-in account · 'share' = getting a link */
  purpose: 'save' | 'share'
  /** Existing names of the user's saved routes (case-insensitive dup check). */
  existingNames?: string[]
  /**
   * 'duplicate'/'error' map to standard messages; any other string shows
   * verbatim. A message starting with LAYOUT_WARNING_PREFIX is a soft
   * warning: it shows in amber and the submit button turns into
   * "Save/Share Anyway", which re-calls onSubmit with force=true.
   */
  onSubmit: (name: string, force: boolean) => Promise<string | null>
  onClose: () => void
}

// Marker the submit handler returns to flag a soft layout warning (vs a hard
// error): it renders amber and unlocks the Save/Share Anyway button.
export const LAYOUT_WARNING_PREFIX = 'Check the route order'

// Small popup that collects the mandatory route name before a route is
// persisted server-side (account save or anonymous share).
export default function RouteNameModal({
  open,
  purpose,
  existingNames = [],
  onSubmit,
  onClose,
}: RouteNameModalProps) {
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [layoutWarned, setLayoutWarned] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!open) {
      setName('')
      setError(null)
      setBusy(false)
      setLayoutWarned(false)
      return
    }
    const t = setTimeout(() => inputRef.current?.focus(), 80)
    return () => clearTimeout(t)
  }, [open])

  if (!open) return null

  const trimmed = name.trim()
  const localDuplicate =
    trimmed !== '' &&
    existingNames.some((n) => n.trim().toLowerCase() === trimmed.toLowerCase())

  const submit = async (force = false) => {
    if (busy) return
    if (!trimmed) {
      setError('Please give this route a name.')
      return
    }
    if (localDuplicate) {
      setError('You already have a saved route with this name.')
      return
    }
    setBusy(true)
    setError(null)
    const result = await onSubmit(trimmed, force)
    setBusy(false)
    if (result === 'duplicate') {
      setError('You already have a saved route with this name.')
    } else if (result === 'error') {
      setError('Something went wrong — please try again.')
    } else if (result) {
      setError(result)
      if (result.startsWith(LAYOUT_WARNING_PREFIX)) setLayoutWarned(true)
    } else {
      onClose()
    }
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-3"
        onClick={() => {
          if (!busy) onClose()
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.97 }}
          transition={{ type: 'spring', damping: 26, stiffness: 300 }}
          className="w-full max-w-sm rounded-xl bg-[#0C0C0E]/95 backdrop-blur border border-white/10 p-4 space-y-3"
          role="dialog"
          aria-modal="true"
          aria-label={purpose === 'save' ? 'Name your route' : 'Name this route to share it'}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-2">
            {purpose === 'save' ? (
              <Bookmark size={16} className="text-amber-400" />
            ) : (
              <Share2 size={16} className="text-amber-400" />
            )}
            <span className="text-[10px] font-mono uppercase tracking-widest text-amber-400">
              {purpose === 'save' ? 'Save route' : 'Share route'}
            </span>
          </div>

          <label htmlFor="route-name-input" className="block text-sm font-semibold text-white">
            Name this route
          </label>
          <input
            id="route-name-input"
            ref={inputRef}
            type="text"
            value={name}
            maxLength={60}
            disabled={busy}
            onChange={(e) => {
              setName(e.target.value)
              if (error) setError(null)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void submit()
              } else if (e.key === 'Escape' && !busy) {
                onClose()
              }
            }}
            placeholder="e.g. Marin loop"
            className="w-full h-11 rounded-lg bg-white/[0.06] border border-white/15 focus:border-amber-400/70 focus:outline-none px-3 text-sm text-white placeholder:text-white/30"
          />

          <p
            className={
              error && error.startsWith(LAYOUT_WARNING_PREFIX)
                ? 'text-[11px] leading-relaxed text-amber-400 font-medium'
                : 'text-[11px] leading-relaxed text-white/50'
            }
            role="alert"
          >
            {error ?? 'Routes are stored under a name so you can tell them apart later.'}
          </p>

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="h-11 px-4 rounded-lg border border-white/15 text-white/70 hover:text-white text-sm font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void submit(layoutWarned)}
              disabled={busy || !trimmed || localDuplicate}
              className="h-11 px-5 rounded-lg bg-amber-400 hover:bg-amber-300 disabled:opacity-40 text-black font-bold text-sm tracking-wide transition-colors"
            >
              {busy
                ? 'Saving…'
                : layoutWarned
                  ? purpose === 'save'
                    ? 'Save Anyway'
                    : 'Share Anyway'
                  : purpose === 'save'
                    ? 'Save'
                    : 'Share'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}
